import type { FC } from 'react'
import React, { useMemo } from 'react'
import { useParams } from 'react-router'
import { CommonLink } from '@frontend/pole-ui/lib/components/CommonLink'
import { useGetLegalDocumentAndVersions } from '@/api/kubik/monolith/hooks/useGetLegalDocumentAndVersions'
import { useGetLegalDocuments } from '@/api/kubik/monolith/hooks/useGetLegalDocuments'
import type { ILegalDocumentContentAnnexe } from '@/components/composed/DocumentContent/interfaces'
import { Skeleton } from '@/components/ui'
import { getLegalDocumentUrl } from '@/logic/legalDocuments/helpers'
import type { IDocumentContentParams } from '@/logic/legalDocuments/interfaces'
import { Redirect404 } from '@/router/components'
import { isNonNullable } from '@/typeguards/common'
import { ELegalDocumentsRoute } from '@/types/ELegalDocumentsRoute'
import type { ILegalDocument } from '@/types/legalDocuments'
import { ELegalDocumentType } from '@/types/legalDocuments'
import { getStaticPath } from '@/utils/getStaticPath'
import { createRouterLink } from '@/utils/url'
import DocumentContent from '../../components/composed/DocumentContent'
import { legalDocumentsSkeletons } from './constants'
import { messages } from './messages'
import './styles.scss'

const LegalDocumentsPage: FC = () => {
  const { documentId, versionId } = useParams<IDocumentContentParams>()
  const { data: legalDocumentsResult } = useGetLegalDocuments()
  const { data: legalDocumentAndVersions, isError, isLoading } = useGetLegalDocumentAndVersions(documentId, versionId)

  const legalDocuments = useMemo(() => (legalDocumentsResult?.data ?? []) as ILegalDocument[], [legalDocumentsResult])

  const selectedLegalDocument = useMemo(() => legalDocuments.find(({ typeId }) => typeId === documentId), [documentId, legalDocuments])
  const isActualDocumentVersion = isNonNullable(selectedLegalDocument) && selectedLegalDocument.id === versionId
  const currentDocumentVersion = legalDocumentAndVersions?.data?.currentVersion
  const previousDocumentVersions = legalDocumentAndVersions?.data?.previousVersions ?? []

  const legalDocumentContentAnnexes: ILegalDocumentContentAnnexe[] = useMemo(() => {
    if (documentId === ELegalDocumentType.registrationRules) {
      return [
        {
          index: 1,
          url: getLegalDocumentUrl(legalDocuments, ELegalDocumentType.registrationPolicy),
        },
        {
          index: 2,
          url: getLegalDocumentUrl(legalDocuments, ELegalDocumentType.cookiesPolicy),
        },
        {
          index: 3,
          url: getLegalDocumentUrl(legalDocuments, ELegalDocumentType.registrationRegulation),
        },
        {
          index: 4,
          url: getStaticPath('documents/attachment-to-rules-of-agromarket-4.pdf'),
          isDocument: true,
        },
        {
          index: 5,
          url: getStaticPath('documents/attachment-to-rules-of-agromarket-5.pdf'),
          isDocument: true,
        },
        {
          index: 6,
          url: getStaticPath('documents/attachment-to-rules-of-agromarket-6.pdf'),
          isDocument: true,
        },
      ]
    }

    return []
  }, [documentId, legalDocuments])

  if (isError) {
    return <Redirect404 />
  }

  return (
    <>
      <h2 className="legal-documents-header page-wrapper">{messages.header}</h2>
      <div className="legal-documents-container page-wrapper page-wrapper_no-mobile">
        {isActualDocumentVersion && (
          <ul className="legal-documents-list">
            {legalDocuments.map(({ typeId, id, name }) => (
              <li className="legal-documents-item" key={id}>
                <CommonLink url={createRouterLink(ELegalDocumentsRoute.DOCUMENT_VERSION, { documentId: typeId, versionId: id })}>
                  {name}
                </CommonLink>
              </li>
            ))}
          </ul>
        )}
        <Skeleton isLoading={isLoading || !currentDocumentVersion || !previousDocumentVersions} skeletons={legalDocumentsSkeletons}>
          {currentDocumentVersion && (
            <DocumentContent
              documentId={documentId}
              isActualDocumentVersion={isActualDocumentVersion}
              content={currentDocumentVersion.content}
              annexes={legalDocumentContentAnnexes}
              previousVersions={previousDocumentVersions}
            />
          )}
        </Skeleton>
      </div>
    </>
  )
}

export default LegalDocumentsPage
